<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import SalesPartyAddressSection from '../components/SalesPartyAddressSection.vue';
import SalesPartyAttorneySection from '../components/SalesPartyAttorneySection.vue';
import SalesPartyCertificateFeedback from '../components/SalesPartyCertificateFeedback.vue';
import SalesPartyCompanySection from '../components/SalesPartyCompanySection.vue';
import SalesPartyDocumentsSection from '../components/SalesPartyDocumentsSection.vue';
import SalesPartyEncumbrancesSection from '../components/SalesPartyEncumbrancesSection.vue';
import SalesPartyOriginSection from '../components/SalesPartyOriginSection.vue';
import SalesPartyPersonalSection from '../components/SalesPartyPersonalSection.vue';
import SalesPartyRegistrationContent from '../components/SalesPartyRegistrationContent.vue';
import SalesPartyRegistrationHeader from '../components/SalesPartyRegistrationHeader.vue';
import SalesPartyReviewCorrectionsDialog from '../components/SalesPartyReviewCorrectionsDialog.vue';
import { useSalesParties } from '../composables/useSalesParties';
import { useSalesPartyForm } from '../composables/useSalesPartyForm';
import { useSalesPartyCatalog } from '../composables/useSalesPartyCatalog';
import { useSalesPartyLookup } from '../composables/useSalesPartyLookup';
import { useSalesPartyLaborCertificateCheck } from '../composables/useSalesPartyLaborCertificateCheck';
import {
  salesPartyOriginRequired,
  useSalesPartyRegistrationPage,
} from '../composables/useSalesPartyRegistrationPage';
import { useSalesReviewCheckCompletion } from '../composables/useSalesReviewCheckCompletion';
import { useSalesRegistration } from '../composables/useSalesRegistration';
import {
  salesPartyReviewContext,
  shouldShowSalesPartyReview,
} from '../composables/salesPartyReviewContext';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() => route.params.salesTransactionId);
const partyId = computed(() => route.params.partyId || null);
const role = computed(() => route.params.partyRole);
const partyKind = computed(() =>
  String(route.params.partyKind || 'pessoa_fisica').replace('-', '_')
);
const parentPartyId = computed(() => route.params.parentPartyId || null);
const relationType = computed(() =>
  parentPartyId.value ? 'representante' : 'principal'
);
const reviewMode = computed(() => shouldShowSalesPartyReview(route));
const correctionsOpen = ref(false);

const registration = useSalesRegistration({
  id: transactionId,
  t,
  notify: useAlert,
});
const roleParties = useSalesParties({
  transactionId,
  role,
  t,
  notify: useAlert,
});
const catalog = useSalesPartyCatalog({
  transactionId,
  t,
  notify: useAlert,
});
const originRequired = salesPartyOriginRequired({
  partyId,
  relationType,
  role,
  roleParties,
});

const form = useSalesPartyForm({
  transactionId,
  partyId,
  partyRole: role,
  partyKind,
  relationType,
  parentPartyId,
  originRequired,
  t,
  notify: useAlert,
  onSaved: () => useAlert(t('SALES_PARTIES.REGISTRATION.SAVED')),
});
const partyLookup = useSalesPartyLookup({
  transactionId,
  profile: form.profile,
});
const laborCertificate = useSalesPartyLaborCertificateCheck({
  transactionId,
  t,
});

const components = {
  personal: SalesPartyPersonalSection,
  company: SalesPartyCompanySection,
  address: SalesPartyAddressSection,
  attorney: SalesPartyAttorneySection,
  encumbrances: SalesPartyEncumbrancesSection,
  origin: SalesPartyOriginSection,
  documents: SalesPartyDocumentsSection,
};
const reviewContext = computed(() =>
  salesPartyReviewContext({
    partyId: partyId.value,
    relationType: relationType.value,
    role: role.value,
    transaction: registration.transaction.value,
  })
);
const reviewCompletion = useSalesReviewCheckCompletion({
  id: transactionId,
  transaction: registration.transaction,
  t,
  notify: useAlert,
});
const page = useSalesPartyRegistrationPage({
  catalog,
  certificateCheck: ({ party }) =>
    laborCertificate.check({ party, partyId: party?.id }),
  components,
  form,
  partyId,
  partyKind,
  parentPartyId,
  registration,
  relationType,
  roleParties,
  reviewCompletion,
  reviewContext,
  reviewMode,
  role,
  route,
  router,
  internalProfile: partyLookup.internalProfile,
  internalProfileAlert: partyLookup.internalProfileAlert,
  lookupError: partyLookup.lookupError,
  lookupLoading: partyLookup.loading,
  spouseLookupError: partyLookup.spouseLookupError,
  spouseLookupLoading: partyLookup.spouseLoading,
  t,
  transactionId,
});
const update = (section, field, value) => {
  page.update(section, field, value);
  if (section === 'identity' && field === 'cpf') partyLookup.lookup(value);
  if (section === 'identity' && field === 'spouse_cpf') {
    partyLookup.lookupSpouse(value);
  }
};
const save = () => page.saveAndExit();

onMounted(async () => {
  await Promise.all([roleParties.load(), form.load(), catalog.load()]);
  page.ensureRepresentativeRoles();
});
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesRegistrationPermissionState
      v-if="registration.isForbidden.value"
      @back="router.back()"
    />
    <template v-else>
      <SalesPartyRegistrationHeader
        :active-label="page.labels.value[form.activeSection.value]"
        :can-edit="page.canEdit.value"
        :page-title="page.pageTitle.value"
        :role-label="page.roleLabel.value"
        :saving="form.saving.value"
        @back="page.goBack"
      />

      <SalesPartyRegistrationContent
        :active-section="form.activeSection.value"
        :can-edit="page.canEdit.value"
        :current-bindings="page.currentBindings.value"
        :current-component="page.currentComponent.value"
        :current-index="form.currentIndex.value"
        :has-party="Boolean(form.party.value?.id)"
        :is-complete="form.sectionComplete"
        :loading="form.loading.value"
        :party-kind="partyKind"
        :progress-percent="form.party.value?.progress?.percent"
        :review-count="reviewContext.checks.length"
        :review-mode="reviewMode"
        :saving="form.saving.value"
        :section-checks="reviewContext.sectionChecks"
        :sections="form.sections.value"
        @changed="form.load"
        @complete-review="page.saveAndExit"
        @next="page.next"
        @previous="form.previous"
        @save="save"
        @select="page.selectSection"
        @show-corrections="correctionsOpen = true"
        @update="update"
      />
      <SalesPartyReviewCorrectionsDialog
        :context="reviewContext"
        :show="correctionsOpen"
        @close="correctionsOpen = false"
      />
      <SalesPartyCertificateFeedback
        :items="laborCertificate.items.value"
        :open="laborCertificate.open.value"
      />
    </template>
  </div>
</template>
